import * as React from "react";
import styled from "styled-components";
import { select, useSelect } from "@hubper/theme";
import { useSpring, animated } from "react-spring";

import Text from "../../elements/Text";
import Icon from "../../elements/Icon";
import SelectMenuItem from "../../elements/SelectMenuItem";
import ToggleFloatingLayer from "../ToggleFloatingLayer";
import FloatingBox from "../../elements/FloatingBox";
import FilterInput from "../../elements/FilterInput";

import highlightText from "../../util/highlightText";

const AnimatedIcon = animated(Icon);

const sortByName = (a: { name: string }, b: { name: string }) => {
  const aName = a.name.toLowerCase();
  const bName = b.name.toLowerCase();

  if (aName > bName) {
    return 1;
  }
  if (aName < bName) {
    return -1;
  }
  return 0;
};

const Base = styled.div`
  user-select: none;
`;

const SelectBase = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;

  & > *:first-child {
    flex: 1;
    margin-right: ${select.space(2)};
  }
`;

type SelectProps = {
  style?: React.CSSProperties;
  className?: string;
  children: string;
  isOpen: boolean;
  onClick: () => void;
};

const Select = React.forwardRef(function Select(
  { style, className, children, isOpen, onClick }: SelectProps,
  ref: any
) {
  const iconStyle = useSpring({
    transform: `rotate(${isOpen ? 180 : 0}deg)`,
    config: {
      tension: 300
    }
  });

  return (
    <SelectBase ref={ref} style={style} className={className} onClick={onClick}>
      <Text
        fs={1}
        fw="bold"
        c={["grey", 8]}
        style={{
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis"
        }}
      >
        {children}
      </Text>
      <AnimatedIcon
        size={20}
        c={["grey", 8]}
        style={iconStyle}
        name="dropdown"
      />
    </SelectBase>
  );
});

const MenuBase = styled.div`
  /* width: 200px; */
  max-height: 300px;
  overflow: auto;
  padding-bottom: ${select.space(1)};
`;

type Props = {
  style?: React.CSSProperties;
  className?: string;
  onSelect: (id: string) => any;
  items: Array<{ id: string; name: string }>;
  id: string;
  label: string;
  filterPlaceholder: string;
};

function ClientSelect(
  { style, className, label, items, id, filterPlaceholder, onSelect }: Props,
  ref: any
) {
  const selectedItem = items.find(item => item.id === id);

  const [filterValue, setFilterValue] = React.useState("");
  const { space } = useSelect();

  const filteredItems = filterValue
    ? items.filter(item =>
        item!.name.toLowerCase().includes(filterValue.toLowerCase())
      )
    : items;

  return (
    <Base ref={ref} style={style} className={className}>
      <Text
        style={{ display: "block", marginBottom: 4 }}
        uppercase
        fs={-2}
        c={["grey", 5]}
      >
        {label}
      </Text>
      <ToggleFloatingLayer
        closeOnOutsideClick
        offset={{ x: 0, y: 7 }}
        layer={
          <FloatingBox style={{ width: 270 }}>
            <div
              style={{
                paddingLeft: space(4),
                paddingRight: space(4),
                paddingTop: space(4),
                paddingBottom: space(0)
              }}
            >
              <FilterInput
                placeholder={filterPlaceholder}
                value={filterValue}
                onChange={(evt: any) => setFilterValue(evt.target.value)}
              />
            </div>
            <MenuBase>
              {filteredItems.sort(sortByName).map(item => (
                <SelectMenuItem
                  selected={item.id === id}
                  onClick={() => onSelect(item.id)}
                  key={item.id}
                  style={{
                    paddingLeft: space(4),
                    paddingRight: space(4)
                  }}
                >
                  {filterValue
                    ? highlightText(item.name as string, filterValue)
                    : item.name}
                </SelectMenuItem>
              ))}
            </MenuBase>
          </FloatingBox>
        }
      >
        {({ toggle, isOpen }) => (
          <Select isOpen={isOpen} onClick={toggle}>
            {selectedItem ? selectedItem.name : ""}
          </Select>
        )}
      </ToggleFloatingLayer>
    </Base>
  );
}

export default React.forwardRef(ClientSelect);
